<script setup>
import { ref } from "vue";

/**
 * About page FAQ list (GH-1145, Figma "Frequently Asked Questions"): tan-ruled
 * rows with a red plus/minus toggle; an open row reveals its answer on a
 * light panel. Rows toggle independently; `openIndex` picks the one that
 * starts open (-1 for none). Answers are trusted HTML from the page itself so
 * they can carry links.
 */
const props = defineProps({
    // [{ question: String, answer: String (HTML) }]
    items: {
        type: Array,
        required: true,
    },
    openIndex: {
        type: Number,
        default: 0,
    },
});

const open = ref(props.items.map((_, index) => index === props.openIndex));

function toggle(index) {
    open.value[index] = !open.value[index];
}
</script>

<template>
    <div>
        <div v-for="(item, index) in items" :key="item.question">
            <h3 class="m-0 border-b border-brand-tan">
                <!--
                    Explicit reset on the native button: the legacy button
                    styles ride along with app.scss on this page.
                -->
                <button
                    type="button"
                    class="flex w-full cursor-pointer appearance-none items-center justify-between gap-4 border-0 bg-transparent py-[18px] pl-4 pr-5 text-left"
                    :aria-expanded="open[index]"
                    :aria-controls="`faq-answer-${index}`"
                    @click="toggle(index)"
                >
                    <span
                        class="text-[18px] font-semibold leading-[1.3] text-black md:text-[23px]"
                    >
                        {{ item.question }}
                    </span>
                    <span
                        class="flex size-[27px] shrink-0 items-center justify-center rounded-full bg-brand-red"
                        aria-hidden="true"
                    >
                        <img
                            :src="
                                asset(
                                    open[index]
                                        ? 'images/brand/icon-faq-minus.svg'
                                        : 'images/brand/icon-faq-plus.svg',
                                )
                            "
                            alt=""
                            class="w-3"
                        />
                    </span>
                </button>
            </h3>
            <div
                v-show="open[index]"
                :id="`faq-answer-${index}`"
                class="bg-[#fafafa] px-[21px] pb-6 pt-[21px]"
            >
                <p
                    class="m-0 max-w-[909px] text-[16px] leading-[20px] text-brand-navy [&_a]:font-semibold [&_a]:text-brand-red [&_a]:underline"
                    v-html="item.answer"
                />
            </div>
        </div>
    </div>
</template>
